import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

import { colors } from 'styles/colors';
import { devices } from 'styles/devices';

const Search = ({ value, onChange, onFocus, onBlur, small, className, handleClose }) => {
   const intl = useIntl();

   return (
      <Wrapper small={small} className={className}>
         <StyledSearchIcon icon={faSearch} />
         {!!value.length && (
            <Button type="button" onClick={handleClose}>
               <StyledCloseIcon icon={faTimes} />
            </Button>
         )}
         <Input
            onFocus={onFocus}
            onChange={onChange}
            onBlur={onBlur}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
            placeholder={intl.formatMessage({ id: 'Search.Placeholder' })}
            type="search"
            value={value}
         />
      </Wrapper>
   );
};

const Button = styled.button`
   position: absolute;
   top: 50%;
   transform: translateY(-50%);
   right: 0;
   border: none;
   padding: 0;
   margin: 0;
   width: 60px;
   height: 60px;
   background: none;

   &:focus {
      outline: 0;
   }

   ${({ small }) =>
      small &&
      css`
         width: 40px;
         height: 40px;
      `}
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
   position: absolute;
   z-index: -1;
   top: 50%;
   transform: translateY(-50%) scale(1.2);
   transition: transform 0.35s;
`;

const StyledSearchIcon = styled(StyledFontAwesomeIcon)`
   left: 0;
   transform: translate(20px, -50%);
`;

const StyledCloseIcon = styled(StyledFontAwesomeIcon)`
   left: 50%;
   transform: translate(-50%, -50%) scale(1.2);
`;

const Input = styled.input`
   display: block;
   width: 100%;
   height: 60px;
   border-radius: 30px;
   transition: all 0.35s ease-in-out;
   transform-origin: right;
   padding: 0 10px 0 50px;
   border: none;
   background-color: transparent;

   &::placeholder {
      color: ${colors.neutral50};
      white-space: pre-line;
      opacity: 0;
      font-size: 1px;

      ${devices.mobile} {
         font-size: 13px;
      }

      ${devices.mobileL} {
         font-size: 16px;
         opacity: 1;
      }
   }

   &:focus {
      outline: 0;
   }

   &::-webkit-search-cancel-button {
      -webkit-appearance: none;
      display: none;
   }
`;

const Wrapper = styled.div`
   position: relative;
   z-index: 500;
   border-radius: 30px;
   height: 60px;
   background-color: ${colors.neutral20};
   transition: all 0.35s ease-in-out;

   ${({ small }) =>
      small &&
      css`
         height: 40px;
         border-radius: 20px;

         ${Input} {
            height: 40px;
            padding: 0 0 0 50px;
         }
      `}

   &:focus-within {
      width: 100%;
   }
`;

Search.propTypes = {
   value: PropTypes.string.isRequired,
   onChange: PropTypes.func.isRequired,
   handleClose: PropTypes.func.isRequired,
   onFocus: PropTypes.func,
   onBlur: PropTypes.func,
   small: PropTypes.bool,
   className: PropTypes.string,
   refInput: PropTypes.shape({
      current: PropTypes.instanceOf(Element)
   })
};

Search.defaultProps = {
   small: false,
   onFocus: null,
   onBlur: null,
   className: '',
   refInput: null
};

export default Search;
